/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { ReactNode } from 'react'
import { Table, TableSortDir } from '../table/Table'
import { useRcmTableData } from '../../hooks/useRcmTableData'
import { GridWrapper } from './GridWrapper'

export interface RcmTableProps {
  className?: string
  gridViewClass?: string
  noResultsText?: string
  hidePaginate?: boolean
  disableOnInitLoad?: boolean
  simplePaginate?: boolean
  TableFooter?: ReactNode
  HeadRow?: ReactNode
  Row?: (props: { row: any; index: number }) => ReactNode
  GridItemView?: React.ComponentType<{ row: any }>
  data?: any[] // External data override
  total?: number // External total override
  blocked?: boolean // External loading state override
  onRowEnter?: (row: any) => void
  rowKey?: string
  [key: string]: any // Allow additional props
}

export interface LoadDataParams {
  sortDir: TableSortDir
  sort: string
  perPage: number
  page: number
}

export interface PagingParams {
  sortDesc: boolean
  sortBy: string
  pageSize: number
  pageStart: number
}

export const RcmTable: React.FC<RcmTableProps> = (props) => {
  const tableData = useRcmTableData()

  const loadDataHandler = (params: LoadDataParams) => {
    const pagingParams: PagingParams = {
      sortDesc: params.sortDir === 'desc',
      sortBy: params.sort,
      pageSize: params.perPage,
      pageStart: params.page - 1
    }
    tableData.load(pagingParams)
  }

  // Handle sorting using the context's setSort method
  const handleSort = (sortName: string, sortDir: 'asc' | 'desc') => {
    tableData.setSort(sortName, sortDir === 'desc')
  }

  // Use external data if provided, otherwise use internal tableData
  const effectiveData = props.data !== undefined ? props.data : tableData.entries
  const effectiveTotal = props.total !== undefined ? props.total : tableData.total
  const effectiveBlocked = props.blocked !== undefined ? props.blocked : tableData.isLoading

  return (
    <>
      {tableData.isGridView ? (
        <GridWrapper
          gridViewClass={props.gridViewClass}
          GridItemView={props.GridItemView!}
          simplePaginate={props.simplePaginate}
        />
      ) : (
        <Table
          className="chita-table"
          noResultsText="NO_RESULTS_FILTERED"
          disableOnInitLoad={true}
          {...props}
          loadData={loadDataHandler}
          onSort={handleSort}
          page={tableData.page}
          hasNextPage={tableData.hasNextPage}
          defaultSortBy={tableData.sortBy}
          defaultSortDesc={tableData.sortDesc}
          data={effectiveData}
          total={effectiveTotal}
          blocked={effectiveBlocked}
          persistanceKey={tableData.persistenceKey || undefined}
        />
      )}
    </>
  )
}
